// src/modules/forecast.js

/**
 * Returns object for populating map
 * @param {Object} items 
 * @param {String} api 
 * @param {String} latitude 
 * @param {String} longitude 
 * @param {String} feature 
 * @param {String} colorscale 
 * @returns 
 */
function getMapObject(items, params, colors, target='oil') {
  const feature = colors.feature.value == 'production' ? target : colors.feature.value
  const color   = colors.colors.value

  return {
    type: "scattermapbox",
    text: items[params.api].map(String),
    lat: items[params.latitude],
    lon: items[params.longitude],
    mode: "markers",
    // hoverinfo: 'none',
    marker: {
      size: 6,
      reversescale: true,
      autocolorscale: false,
      color: items[feature],
      colorscale: color,
      cmin: Math.min(...items[target]),
      cmax: Math.max(...items[target]),
      colorbar: {
        thickness: 10,
        titleside: "right",
        outlinecolor: "rgba(0,0,0,0)",
        ticks: "outside",
        ticklen: 3,
        x: 0.9,
        y: 0.5,
        bgcolor: "rgba(0,0,0,0)",
      },
    },
    unselected: {
      marker: {
        // size: 12,
        // color: '#607D8B',
        opacity: 0.1
      }
    }
  }
}
/**
 * Returns array for populating well profiles
 * @param {Object} production 
 * @param {String} calculation 
 * @returns 
 */
function getProfileObjects(records, months, calculation) {
  let plots = []
  // Push individual wells
  for(let[id, well] of Object.entries(records)) {
    plots.push({
      name: id,
      type: "scatter",
      mode: 'lines',
      showlegend: false,
      y: calculation == 'cumulative' ? _cumulative(Object.values(well)) : Object.values(well),
      line: { shape: 'spline', dash: "solid", width: 1 },
    })
  }
  // Push well count
  let count = Object.values(months).map(p => Object.values(p).filter(e => e !== null).length)
  plots.push({
    name: `Well Count`,
    type: "scatter",
    mode: 'lines',
    showlegend: true,
    y: calculation == 'cumulative' ? _cumulative(count) : count,
    yaxis: "y2",
    line: { color: '#000', dash: "solid", width: 2 },
  })
  return plots
}
/**
 * Applies ARPS function to array with parameters
 * @param {Number} months 
 * @param {Number} Q 
 * @param {Number} D 
 * @param {Number} B 
 * @returns {Array}
 */
function declineWell(months, Q, D, B) {
  return Array.from(Array(months), (D, i) => i+1).map(t => arps(Q, D, B, t))
}
/**
 * 
 * @param {Number} months 
 * @param {Array} Q 
 * @param {Array} D 
 * @param {Array} B 
 * @param {Number} quantile 
 * @returns {Array}
 */
function typeCurves(matrix, months, quantile) {
  // let matrix = []
  // Object.entries(wells).map(([well, data]) => 
  //   matrix.push(data.production.concat(data.predictions, data.decline.slice(data.production.length + data.predictions.length)))
  // )
  return [...Array(months).keys()].map(i =>
    _quantile(matrix.map((value, index) => value[i]), quantile)
  )
}
/**
 * Calculate quantiles of array
 * @param {Array} arr Input array
 * @param {Number} q Quantile
 * @returns Number
 */
function _quantile(arr, q)  {
  if (!Array.isArray(arr)) return arr
  const sorted = arr.sort(function(a, b){return a - b})
  const pos = (sorted.length - 1) * q
  const base = Math.floor(pos)
  const rest = pos - base
  if (sorted[base + 1] !== undefined) {
      return Math.round(sorted[base] + rest * (sorted[base + 1] - sorted[base]))
  } else {
      return Math.round(sorted[base])
  }
}
/**
 * Capitlizes first letter of string
 * @param {String} text 
 * @returns {String}
 */
function _capitalize(text) {
  return text.charAt(0).toUpperCase() + text.slice(1)
}
/**
 * Returns index for last non-NA/null value.
 * @param {Array} arr Input array
 * @returns Number
 */
function _last_valid_index(arr) {
  var index = arr.length
  while (index-- && !arr[index])
  return index + 1
}
/**
 * Return cumulative total array
 * @param {Array} arr
 * @param {Boolean} cumulative
 * @returns {Array}
 */
function _cumulative(arr) {
  return arr.slice(0, _last_valid_index(arr)).map((s => a => s += a)(0))
}
/**
 * Calculate instantaneous flow rate of a hyperbolic or exponential decline curve at time t
 * 
 * @param {Number} Q Initial rate (constant)
 * @param {Number} D Initial decline rate (constant)
 * @param {Number} B b value (constant)
 * @param {Number} t time at which you want to calculate the rate (variable)
 * @returns {Number} Instantaneous flow rate
 */
function arps(Q, D, B, t) {
  if (B == 0) { // Exponential decline
    return Math.round(Q * Math.exp(-(D) * t))
  } else if (B == 1) { // Harmonic decline
    return Math.round(Q / (1 + D * t))
  } else { // Hyperbolic decline
    return Math.round(Q / ((1 + B * D * t) ** (1 / B)))
  }
}
/**
 * Calculate instantaneous decline rate of ARPS decline equation at time t
 * 
 * @param {Number} D initial decline rate
 * @param {Number} B decline exponent
 * @param {Number} t time in which you want to calculate the decline rate
 * @param {Boolean} rounded Whether to round output or not
 * @returns {Number} Instananeous decline rate
 */
function arps_decline(D, B, t, rounded=true) {
  const output = (D / (1 + B * D * t)) * 100
  if (rounded) return Math.round((output + Number.EPSILON) * 100) / 100
  return output
}
/**
 * Calculate the cumulative rate from an initial rate and final rate
 * 
 * @param {Number} Q Initial flow rate
 * @param {Number} D Initial decline rate
 * @param {Number} B Decline exponent
 * @param {Number} Z Final production flow rate 
 * @returns {Number} Total estimated reserves
 */
function _arps_reserves(Q, D, B, Z) {
  if (B == 1) {
    return (Q / D) * Math.log(Q / Z)
  } else {
    return ((Q ** B) / (D * (B - 1))) * ((Q ** (1 - B)) - (Z ** (1 - B)))
  }
}
/**
 * Calculate distance between two points
 * @param {Number} lat1 
 * @param {Number} lon1 
 * @param {Number} lat2 
 * @param {Number} lon2 
 * @param {String} unit Defaults to [M]iles also [K]ilometers, and [N]autical miles
 * @returns {Number}
 */
function getDistanceFromLatLon(lat1, lon1, lat2, lon2, unit='M') {
  // Return 0 if same points
  if ((lat1 == lat2) && (lon1 == lon2)) {
    return 0
  }
  else {
    var radlat1 = Math.PI * lat1/180
    var radlat2 = Math.PI * lat2/180
    var theta = lon1-lon2
    var radtheta = Math.PI * theta/180
    var dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta)
    if (dist > 1) {
        dist = 1
    }
    dist = Math.acos(dist)
    dist = dist * 180/Math.PI
    dist = dist * 60 * 1.1515
    if (unit=="K") { dist = dist * 1.609344 }
    if (unit=="N") { dist = dist * 0.8684 }
    return dist
  }
}

export { getMapObject, getProfileObjects, declineWell, typeCurves, getDistanceFromLatLon, arps, arps_decline }
